import React from 'react';
import { connect } from 'react-redux';
import styled, { css } from 'styled-components';

import Box from 'Components/Base/Box';
import ButtonOutline from 'Components/Base/Button/ButtonOutlined';
import Flex from 'Components/Base/Flex';
import Image from 'Components/Base/Image';
import Text from 'Components/Base/Text';
import {
  useCheckFreeTrialEnrollmentEligible,
  useCreateFreeTrialEnrollment,
  useGetFreeTrialCourses,
} from 'Domain/EggSpace';
import { checkFreeTrialEnrollmentEligibility } from 'GraphQL/query/Enrollment.query';
import { myProfile } from 'Redux/user-profile/selectors';
import useCompatibleDisplay from 'Util/Hooks/useCompatibleDisplay';

import FreeTrialCourseEnrollmentPendingModal from '../../Components/FreeTrialCourseEnrollmentPendingModal';

import EggSpaceOnboardingContainer from './EggSpaceOnboardingContainer';

const BaseBtnCss = css`
  height: 40px;
  min-width: 90px;
  max-width: 90px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background-color: #fff;
`;

const StyledButtonOutlined = styled(ButtonOutline)`
  ${BaseBtnCss};
`;

function EggSpaceOnboardingBanner({ user, courses, tips, subtitle }) {
  const [open, setOpen] = React.useState(false);
  const [openPendingModal, setOpenPendingModal] = React.useState(false);
  const { isMobile } = useCompatibleDisplay();

  const { data: checkFreeTrialEnrollmentEligibleData } = useCheckFreeTrialEnrollmentEligible();
  const { data: getFreeTrialCoursesData, loading: getFreeTrialCoursesLoading } =
    useGetFreeTrialCourses();
  const {
    mutate: createFreeTrialEnrollment,
    loading,
    error,
  } = useCreateFreeTrialEnrollment({
    refetchQueries: [{ query: checkFreeTrialEnrollmentEligibility }],
    awaitRefetchQueries: true,
  });

  const eggSpaceEligible = checkFreeTrialEnrollmentEligibleData?.isEligible ?? false;
  const userId = user?.id ? String(user.id) : '';

  // ✅ keep "seen" list in state (reactive)
  const [seenUserIds, setSeenUserIds] = React.useState([]);

  React.useEffect(() => {
    try {
      const raw = localStorage.getItem('SEEN_EGG_SPACE_ONBOARDING_USER_IDS') || '[]';
      const parsed = JSON.parse(raw);
      setSeenUserIds(Array.isArray(parsed) ? parsed : []);
    } catch {
      setSeenUserIds([]);
    }
  }, []);

  const hasAllFreeTrialCourses = React.useMemo(() => {
    if (!getFreeTrialCoursesData?.courses || !courses) return false;

    return getFreeTrialCoursesData.courses.every((courseCode) =>
      (courses ?? []).some((c) => c.courseInfo.courseCode.includes(courseCode))
    );
  }, [getFreeTrialCoursesData?.courses, courses]);

  // ✅ decide open based on eligibility + seen list + userId
  React.useEffect(() => {
    if (!userId) return; // user not ready yet

    if (!eggSpaceEligible) {
      return;
    }

    const shouldShow = eggSpaceEligible && !seenUserIds.includes(userId);

    // if user already has all free trial courses, don't open
    if (hasAllFreeTrialCourses) {
      setOpen(false);
      return;
    }

    setOpen(shouldShow);
  }, [userId, seenUserIds, hasAllFreeTrialCourses, eggSpaceEligible]);

  const onClick = () => {
    if (eggSpaceEligible) {
      setOpen(true);
    } else {
      setOpenPendingModal(true);
    }
  };

  const onClose = React.useCallback(() => {
    if (!userId) return;

    setSeenUserIds((prev) => {
      const next = prev.includes(userId) ? prev : [...prev, userId];
      try {
        localStorage.setItem('SEEN_EGG_SPACE_ONBOARDING_USER_IDS', JSON.stringify(next));
      } catch {
        // ignore storage errors
      }
      return next;
    });

    setOpen(false);
  }, [userId]);

  const onEnroll = () => {
    createFreeTrialEnrollment();
  };

  const onClosePendingModal = () => {
    setOpenPendingModal(false);
  };

  // ✅ if user already has everything, hide the whole component
  if (hasAllFreeTrialCourses || getFreeTrialCoursesLoading) return null;

  return (
    <>
      <Flex
        backgroundColor="#FFFBE8"
        justifyContent="center"
        alignItems="center"
        gridColumnGap={isMobile ? 4 : 6}
        height="70px"
      >
        {!isMobile && (
          <Image
            src="/assets/images/myCoursesBanner/eggSpace/TK-onboard-freetrial-banner.svg"
            alt="Egg Space Onboarding Banner"
            style={{ transform: 'translateY(5px)' }}
          />
        )}
        <Box>
          <Text textStyle="heading5" style={{ fontSize: '18px' }}>
            อัปเกรดการเรียน TCAS แบบ
            <Text as="span" color="primary">
              ใหม่!
            </Text>
          </Text>
          <Text textStyle="caption" color="textColor.secondary" style={{ fontSize: '10px' }}>
            {subtitle}
          </Text>
        </Box>
        <StyledButtonOutlined onClick={onClick}>ลองเรียน!</StyledButtonOutlined>
      </Flex>

      <EggSpaceOnboardingContainer
        subtitle={subtitle}
        open={open}
        onClose={onClose}
        onEnroll={onEnroll}
        loading={loading}
        error={error}
      />

      <FreeTrialCourseEnrollmentPendingModal
        primaryOnClick={onClosePendingModal}
        icon={
          <Image
            src="/assets/images/myCoursesBanner/eggSpace/clock.svg"
            alt="Egg Space Onboarding Banner"
          />
        }
        tips={tips}
        muiProps={{
          onClose: onClosePendingModal,
          modalPaperStyle: { maxWidth: 336, textAlign: 'center' },
          isActive: openPendingModal,
        }}
      />
    </>
  );
}

export default connect((state) => ({ user: myProfile(state) }), {})(EggSpaceOnboardingBanner);
